import CustomNavbar from "../reusables/CustomNavbar/CustomNavbar";
import luxiFaded from "../../assets/luxi-blue-faded.png";
import { InsightsTable } from "../InsightsV6/InsightsTable";
import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { getAbTestsOfSiteId, getPfpsByUserIds } from "../../helpers";
import { Spinner } from "react-bootstrap";
import { SelectedProjectContext } from "../../contexts";

const AbTestList = () => {
  const navigate = useNavigate();
  const selectedProject = useContext(SelectedProjectContext);

  const [loading, setLoading] = useState(true);
  const [filteredTests, setFilteredTests] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [sortOrder, setSortOrder] = useState("asc");
  const [tests, setTests] = useState([]);
  const [tabItems, setTabItems] = useState([]);

  const [pfpMap, setPfpMap] = useState(new Map());

  useEffect(() => {
    setLoading(true);
    setTests([]);
    setTabItems([]);

    if (!selectedProject.trackingStartDate) {
      setLoading(false);
      return;
    }

    getAbTestsOfSiteId(selectedProject.value).then((tests) => {
      setTests(tests);
      setFilteredTests(tests);
      setTabItems([
        {
          name: "All",
          label: `All (${tests?.length ?? 0})`,
        },
        {
          name: "In progress",
          label: `In progress (${
            tests?.filter((test) => test.status === "in-progress")?.length ?? 0
          })`,
        },
        {
          name: "Done",
          label: `Done (${
            tests?.filter((test) => test.status === "done")?.length ?? 0
          })`,
        },
      ]);
      setLoading(false);

      const userIds = [...new Set(tests.map((test) => test.userId))];
      getPfpsByUserIds(userIds).then((pfps) => {
        setPfpMap(pfps);
      });
    });
  }, [selectedProject]);

  function sortByField(field, order) {
    const sorted = [...filteredTests];

    if (["num", "title", "status", "userId"].includes(field)) {
      sorted.sort((a, b) => {
        if (a[field] < b[field]) {
          return order === "asc" ? -1 : 1;
        }
        if (a[field] > b[field]) {
          return order === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredTests(sorted);
    if (order === "asc") {
      setSortOrder("desc");
    } else {
      setSortOrder("asc");
    }
  }

  useEffect(() => {
    if (!activeTab) return;

    setSortOrder("desc");
    if (activeTab === "All") {
      setFilteredTests(tests);
    } else if (activeTab === "In progress") {
      setFilteredTests(tests.filter((test) => test.status !== "done"));
    } else if (activeTab === "Done") {
      setFilteredTests(tests.filter((test) => test.status === "done"));
    }
  }, [activeTab]);

  const tableWidths = ["60px", "20px", "2804px", "120px", "260px"];
  const tableMinWidths = ["60px", "20px", "200px", "120px", "260px"];

  const tableHeaders = [
    {
      title: "",
      icon: <i></i>,
      field: "checkbox",
      render: (row) => (
        <div
          className="v6-insights-checkbox"
          onClick={() => {}}
          style={{ cursor: "pointer" }}
        >
          <i
            className={
              false
                ? "fa-solid fa-square-check checked_checkbox"
                : "fa-light fa-square"
            }
          ></i>
        </div>
      ),
    },
    {
      title: "",
      icon: <i className="fa-thin fa-hashtag fs-16 ml--10"></i>,
      field: "num",
      render: (row) => <div className="row-number">{row.num}</div>,
      sort: () => sortByField("num", sortOrder),
    },
    {
      title: "Title",
      className: "fs-16 fc-grey ml--10",
      icon: <></>,
      field: "title",
      render: (row) => <div className="row-title">{row.title}</div>,
      sort: () => sortByField("title", sortOrder),
    },
    {
      title: "Origin",
      icon: (
        <i
          className="fa-thin fa-stars icon"
          style={{ marginBottom: "0px" }}
        ></i>
      ),
      paddingTop: "23px",
      field: "userId",
      sort: () => sortByField("userId", sortOrder),
      render: (row) =>
        pfpMap instanceof Map && pfpMap?.get(row.userId) ? (
          <img
            src={pfpMap?.get(row.userId)}
            className="img-circle-28 ml-10"
            alt="Test author"
          ></img>
        ) : (
          <Spinner className="img-circle-28 ml-10"></Spinner>
        ),
    },
    {
      title: "Status",
      icon: (
        <i
          className="fa-light fa-timer icon"
          style={{ marginBottom: "0px" }}
        ></i>
      ),

      render: (row) => (
        <div className="fc-grey fs-14">
          <div className="fvc ml-10">
            <i
              className={`fa-regular fa-${row.status === "done" ? "circle-check" : "hourglass-start"} icon mr-8`}
              style={{ marginBottom: "0px" }}
            ></i>
            {row.status === "done" ? "Done" : "Running"}
          </div>
        </div>
      ),
      sort: () => sortByField("status", sortOrder),
    },
  ];

  const StartAbTestBtn = () => (
    <button
      onClick={() =>
        navigate("/ab-test/setup", {
          state: {
            activeSelectors: tests.map((test) => test.selector),
          },
        })
      }
      className="btn-primary"
    >
      Start new A/B test
    </button>
  );

  if (loading) {
    return (
      <div
        style={{ height: "112px" }}
        className="d-flex align-items-center justify-content-center"
      >
        <Spinner />
      </div>
    );
  }

  if (!selectedProject?.trackingStartDate) {
    return (
      <div className="abtest-container">
        <div className="fw-700 fs-24 fc-black mb-20">A/B Test</div>
        <div className="abtest-dashboard-description fc-black mb-32">
          A/B testing requires at least one page with the tracking code.
        </div>
        <button
          onClick={() => {
            navigate(
              `/send-code?type=none&siteid=${selectedProject.matomoId}&sample=10`,
            );
          }}
          className="btn-septenary fw-700"
        >
          Install the script
        </button>
      </div>
    );
  }

  return (
    <div className="abtest-container">
      <div className="d-flex justify-content-between align-items-center">
        <div className="fw-700 fs-24 fc-black mb-20">A/B Test</div>
        <StartAbTestBtn />
      </div>
      <div className="abtest-dashboard-description fc-black">
        Luxifer is analyzing all the opportunities that could improve user
        experience directly based on your data 🔥
      </div>
      <div className="mb-32">
        {tabItems && tabItems.length > 0 && (
          <CustomNavbar
            tabItems={tabItems}
            hasHr={true}
            changeOnClick={true}
            bubbleUpSelection={setActiveTab}
          />
        )}
      </div>

      {filteredTests.length > 0 ? (
        <InsightsTable
          widths={tableWidths}
          headers={tableHeaders}
          rows={filteredTests}
          minWidths={tableMinWidths}
          digits={1}
          openModal={(row) =>
            navigate("/ab-test/setup", {
              state: {
                selectedTest: row,
              },
            })
          }
        />
      ) : (
        <div className="no-results-container">
          <div className="no-results-container-content">
            <img
              src={luxiFaded}
              style={{ width: "115px", marginRight: "32px" }}
            ></img>
            <div>
              <div className="fc-black2 fw-500 fs-18">
                There are no A/B tests here.
              </div>
              <div className="fc-black2 fw-500 fs-18">Start one now!</div>
              <div className="no-results-btn-content">
                <StartAbTestBtn />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbTestList;
